import { useEffect, useMemo, useState } from 'react';
import { BackToTopPill } from "@/components/BackToTopPill";
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftRight, Edit, Filter, Plus, RefreshCw, Search, Trash2, Truck } from 'lucide-react';
import RoleGuard from '@/components/auth/RoleGuard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/ui/status-badge';
import { EmptyState } from '@/components/shared/EmptyState';
import { PageErrorState } from '@/components/shared/PageErrorState';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { PageActionBar, PageActionBarDivider } from '@/components/layout/PageActionBar';
import {
  type ShipmentStatus,
  shipmentStatusClassName,
  shipmentStatusStyle,
} from '@/features/shipments/utils/status-style';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/trpc';
import type { RouterOutputs } from '@/lib/trpc';
import { useLocation } from '@/contexts/LocationContext';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

type ShipmentListItem = RouterOutputs['shipments']['listShipments']['items'][number];
type Forwarder = RouterOutputs['shipments']['listForwarders'][number];

type ShipmentsTab = 'shipments' | 'forwarders';

const STATUS_OPTIONS: Array<{ value: ShipmentStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const INLINE_STATUS_OPTIONS: Array<{ value: ShipmentStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

type ForwarderFormState = {
  id?: string;
  name: string;
  email: string;
  phone: string;
  default_currency: string | null;
  is_active: boolean;
};

const EMPTY_FORWARDER_FORM: ForwarderFormState = {
  name: '',
  email: '',
  phone: '',
  default_currency: null,
  is_active: true,
};

const getTabFromSearchParams = (params: URLSearchParams): ShipmentsTab =>
  params.get('tab') === 'forwarders' ? 'forwarders' : 'shipments';

export default function Shipments() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const utils = trpc.useUtils();
  // `allLocations`, not `locations`: the lookup below RESOLVES A NAME on
  // stored shipment rows, whose destination may since have been delegated.
  const { selectedLocation, allLocations } = useLocation();
  const { format: formatMoney } = useCurrencyFormatter();

  usePageTitle('Shipments', 'Track inbound shipments and link them to purchase orders');

  const [tab, setTab] = useState<ShipmentsTab>(() => getTabFromSearchParams(searchParams));

  // Shipments filters
  const [statusFilter, setStatusFilter] = useState<ShipmentStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Inline status update
  const [actioningShipmentId, setActioningShipmentId] = useState<string | null>(null);
  const updateShipmentStatus = trpc.shipments.updateShipment.useMutation({
    onSuccess: async () => {
      await utils.shipments.listShipments.invalidate();
      setActioningShipmentId(null);
      toast({ title: 'Status updated' });
    },
    onError: (err) => {
      setActioningShipmentId(null);
      toast({ title: 'Failed to update status', description: err.message, variant: 'destructive' });
    },
  });

  const handleInlineStatusChange = (shipment: ShipmentListItem, newStatus: ShipmentStatus) => {
    if (newStatus === shipment.status) return;
    setActioningShipmentId(shipment.id);
    updateShipmentStatus.mutate({ shipmentId: shipment.id, status: newStatus });
  };

  // Forwarders state
  const [forwarderDialogOpen, setForwarderDialogOpen] = useState(false);
  const [forwarderForm, setForwarderForm] = useState<ForwarderFormState>(EMPTY_FORWARDER_FORM);
  const [confirmDeactivate, setConfirmDeactivate] = useState<Forwarder | null>(null);

  const locationLookup = useMemo(
    () => new Map(allLocations.map((l) => [l.id, l.name])),
    [allLocations],
  );

  useEffect(() => {
    const nextTab = getTabFromSearchParams(searchParams);
    setTab((prev) => (prev === nextTab ? prev : nextTab));
  }, [searchParams]);

  const shipmentsQuery = trpc.shipments.listShipments.useQuery(
    {
      locationId: selectedLocation?.id ?? null,
      status: statusFilter === 'all' ? undefined : statusFilter,
      search: search.trim() ? search.trim() : null,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    },
    {
      staleTime: 30_000,
    }
  );

  const forwardersQuery = trpc.shipments.listForwarders.useQuery(
    { includeInactive: true },
    {
      staleTime: 60_000,
      enabled: tab === 'forwarders',
    }
  );

  const createForwarder = trpc.shipments.createForwarder.useMutation({
    onSuccess: async () => {
      await utils.shipments.listForwarders.invalidate();
      setForwarderDialogOpen(false);
      setForwarderForm(EMPTY_FORWARDER_FORM);
      toast({ title: 'Forwarder created' });
    },
    onError: (err) => toast({ title: 'Failed to create forwarder', description: err.message, variant: 'destructive' }),
  });

  const updateForwarder = trpc.shipments.updateForwarder.useMutation({
    onSuccess: async () => {
      await utils.shipments.listForwarders.invalidate();
      setForwarderDialogOpen(false);
      setForwarderForm(EMPTY_FORWARDER_FORM);
      toast({ title: 'Forwarder updated' });
    },
    onError: (err) => toast({ title: 'Failed to update forwarder', description: err.message, variant: 'destructive' }),
  });

  const setForwarderActive = trpc.shipments.setForwarderActive.useMutation({
    onSuccess: async () => {
      await utils.shipments.listForwarders.invalidate();
      setConfirmDeactivate(null);
    },
    onError: (err) => toast({ title: 'Failed to update forwarder status', description: err.message, variant: 'destructive' }),
  });

  const openNewForwarder = () => {
    setForwarderForm(EMPTY_FORWARDER_FORM);
    setForwarderDialogOpen(true);
  };

  const openEditForwarder = (forwarder: Forwarder) => {
    setForwarderForm({
      id: forwarder.id,
      name: forwarder.name ?? '',
      email: forwarder.email ?? '',
      phone: forwarder.phone ?? '',
      default_currency: forwarder.default_currency ?? null,
      is_active: Boolean(forwarder.is_active ?? true),
    });
    setForwarderDialogOpen(true);
  };

  const submitForwarderForm = () => {
    const payload = {
      name: forwarderForm.name.trim(),
      email: forwarderForm.email.trim() ? forwarderForm.email.trim() : null,
      phone: forwarderForm.phone.trim() ? forwarderForm.phone.trim() : null,
      default_currency: forwarderForm.default_currency?.trim() ? forwarderForm.default_currency.trim() : null,
      is_active: forwarderForm.is_active,
    };

    if (!payload.name) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }

    if (forwarderForm.id) {
      updateForwarder.mutate({
        forwarderId: forwarderForm.id,
        updates: payload,
      });
      return;
    }

    createForwarder.mutate(payload);
  };

  const shipments = shipmentsQuery.data?.items ?? [];
  const shipmentsTotal = shipmentsQuery.data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(shipmentsTotal / pageSize));

  const handleTabChange = (nextTab: ShipmentsTab) => {
    setTab(nextTab);
    const next = new URLSearchParams(searchParams);
    if (nextTab === 'forwarders') {
      next.set('tab', 'forwarders');
    } else {
      next.delete('tab');
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <RoleGuard requirePurchaseOrderAccess>
      <div className="flex flex-col">
        <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6">
          <Tabs value={tab} onValueChange={(value) => handleTabChange(value as ShipmentsTab)}>
            <PageActionBar
              ariaLabel="Shipments actions"
              left={
                <>
                  <TabsList className="h-8">
                    <TabsTrigger value="shipments" className="h-6 text-xs">Shipments</TabsTrigger>
                    <TabsTrigger value="forwarders" className="h-6 text-xs">Forwarders</TabsTrigger>
                  </TabsList>
                  {tab === 'shipments' && (
                    <>
                      <PageActionBarDivider />
                      <Filter className="h-4 w-4 text-muted-foreground self-center" aria-hidden="true" />
                      <Select value={statusFilter} onValueChange={(value) => {
                        setStatusFilter(value as ShipmentStatus | 'all');
                        setPage(1);
                      }}>
                        <SelectTrigger className="h-8 w-[160px] text-xs">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="relative w-56">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search shipment number..."
                          value={search}
                          onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                          }}
                          className="h-8 pl-9 text-xs"
                        />
                      </div>
                    </>
                  )}
                </>
              }
              right={
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-3 text-xs gap-1.5"
                    onClick={() => (tab === 'shipments' ? shipmentsQuery.refetch() : forwardersQuery.refetch())}
                    disabled={shipmentsQuery.isFetching || forwardersQuery.isFetching}
                  >
                    <RefreshCw
                      className={cn(
                        'h-3.5 w-3.5',
                        (shipmentsQuery.isFetching || forwardersQuery.isFetching) && 'animate-spin'
                      )}
                    />
                    Refresh
                  </Button>
                  {tab === 'shipments' ? (
                    <Button
                      size="sm"
                      className="h-8 px-3 text-xs gap-1.5"
                      onClick={() => navigate('/shipments/new')}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      New shipment
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className="h-8 px-3 text-xs gap-1.5"
                      onClick={openNewForwarder}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add forwarder
                    </Button>
                  )}
                </>
              }
            />

            <TabsContent value="shipments" className="space-y-4">
              <div className="flex justify-end">
                <div className="text-xs text-muted-foreground">
                  {selectedLocation ? `Location: ${selectedLocation.name}` : 'All locations'}
                </div>
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Shipment</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Ship Date</TableHead>
                      <TableHead>ETA</TableHead>
                      <TableHead>Forwarder</TableHead>
                      <TableHead>Purchase Orders</TableHead>
                      <TableHead className="text-right">Freight</TableHead>
                      <TableHead>Location</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shipmentsQuery.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                          Loading shipments...
                        </TableCell>
                      </TableRow>
                    ) : shipmentsQuery.error ? (
                      <TableRow>
                        <TableCell colSpan={8} className="p-0">
                          <PageErrorState
                            title="Couldn't load shipments"
                            description={shipmentsQuery.error.message}
                            onRetry={() => shipmentsQuery.refetch()}
                            retrying={shipmentsQuery.isFetching}
                          />
                        </TableCell>
                      </TableRow>
                    ) : shipments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="p-0">
                          <EmptyState
                            icon={Truck}
                            title="No shipments yet"
                            description="Shipments you create will appear here."
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      shipments.map((shipment: ShipmentListItem) => {
                        const status = (shipment.status ?? 'pending') as ShipmentStatus;
                        const isMutating = actioningShipmentId === shipment.id;
                        const showSelect = status !== 'cancelled';
                        const pos = shipment.purchase_orders ?? [];

                        return (
                          <TableRow
                            key={shipment.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => navigate(`/shipments/${shipment.id}`)}
                          >
                            <TableCell className="font-medium">
                              <Link
                                to={`/shipments/${shipment.id}`}
                                className="text-primary hover:underline"
                                onClick={(event) => event.stopPropagation()}
                              >
                                {shipment.shipment_number ?? '-'}
                              </Link>
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              {showSelect ? (
                                <Select
                                  value={status}
                                  onValueChange={(value) => handleInlineStatusChange(shipment, value as ShipmentStatus)}
                                  disabled={isMutating}
                                >
                                  <SelectTrigger
                                    className={cn(
                                      'w-[130px] h-8 border-0 text-[11px] font-medium',
                                      shipmentStatusClassName(status)
                                    )}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {INLINE_STATUS_OPTIONS.map((opt) => (
                                      <SelectItem key={opt.value} value={opt.value} className="text-[11px]">
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <StatusBadge
                                  status={shipmentStatusStyle(status)}
                                  className="w-[130px] h-8 px-3 text-[11px]"
                                />
                              )}
                            </TableCell>
                            <TableCell>{formatDate(shipment.shipped_date ?? null)}</TableCell>
                            <TableCell>{formatDate(shipment.expected_delivery_date ?? null)}</TableCell>
                            <TableCell>
                              {shipment.freight_forwarder?.name ?? '-'}
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              {pos.length === 0 ? (
                                <span className="text-muted-foreground">-</span>
                              ) : (
                                <div className="flex flex-col gap-0.5">
                                  {pos.map((po) => (
                                    <Link
                                      key={po.id}
                                      to={`/orders/${po.id}`}
                                      className="text-sm text-primary hover:underline truncate max-w-[200px]"
                                    >
                                      {po.po_number ?? 'Untitled'}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {shipment.freight_cost == null
                                ? '-'
                                : formatMoney(shipment.freight_cost, shipment.currency ?? null)}
                            </TableCell>
                            <TableCell>
                              {locationLookup.get(shipment.location_id) ?? '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {shipmentsTotal > 0 && (
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground">
                    Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, shipmentsTotal)} of {shipmentsTotal}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      Prev
                    </Button>
                    <div className="text-xs text-muted-foreground">
                      Page {page} / {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="forwarders" className="space-y-4">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {forwardersQuery.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                          Loading forwarders...
                        </TableCell>
                      </TableRow>
                    ) : forwardersQuery.error ? (
                      <TableRow>
                        <TableCell colSpan={6} className="py-10 text-center text-destructive">
                          {forwardersQuery.error.message}
                        </TableCell>
                      </TableRow>
                    ) : (forwardersQuery.data ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                          No forwarders found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (forwardersQuery.data ?? []).map((forwarder: Forwarder) => {
                        const isActive = Boolean(forwarder.is_active ?? true);
                        return (
                          <TableRow key={forwarder.id} className="hover:bg-muted/50">
                            <TableCell className="font-medium">{forwarder.name ?? '-'}</TableCell>
                            <TableCell>{forwarder.email ?? '-'}</TableCell>
                            <TableCell>{forwarder.phone ?? '-'}</TableCell>
                            <TableCell>{forwarder.default_currency ?? 'Org default'}</TableCell>
                            <TableCell>
                              <Badge variant={isActive ? 'success' : 'secondary'}>
                                {isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={() => openEditForwarder(forwarder)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {isActive ? (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => setConfirmDeactivate(forwarder)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setForwarderActive.mutate({ forwarderId: forwarder.id, isActive: true })}
                                    disabled={setForwarderActive.isPending}
                                  >
                                    <ArrowLeftRight className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </main>
      </div>

      {/* Forwarder Create/Edit */}
      <Dialog open={forwarderDialogOpen} onOpenChange={setForwarderDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{forwarderForm.id ? 'Edit Forwarder' : 'Add Forwarder'}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Name</Label>
              <Input
                value={forwarderForm.name}
                onChange={(e) => setForwarderForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Forwarder name"
              />
            </div>

            <div className="grid gap-2">
              <Label>Email (comma-separated)</Label>
              <Input
                value={forwarderForm.email}
                onChange={(e) => setForwarderForm((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="ops@example.com, accounts@example.com"
              />
            </div>

            <div className="grid gap-2">
              <Label>Phone</Label>
              <Input
                value={forwarderForm.phone}
                onChange={(e) => setForwarderForm((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="+61 ..."
              />
            </div>

            <div className="grid gap-2">
              <Label>Default Currency</Label>
              <Select
                value={forwarderForm.default_currency ?? 'AUD'}
                onValueChange={(value) =>
                  setForwarderForm((prev) => ({
                    ...prev,
                    default_currency: value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AUD">AUD</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="CNY">CNY</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="space-y-0.5">
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-muted-foreground">
                  Inactive forwarders remain in history but can’t be selected by default.
                </div>
              </div>
              <Switch
                checked={forwarderForm.is_active}
                onCheckedChange={(checked) => setForwarderForm((prev) => ({ ...prev, is_active: Boolean(checked) }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setForwarderDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitForwarderForm}
              disabled={createForwarder.isPending || updateForwarder.isPending}
            >
              {forwarderForm.id ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate confirm */}
      <AlertDialog open={!!confirmDeactivate} onOpenChange={(open) => !open && setConfirmDeactivate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate forwarder?</AlertDialogTitle>
            <AlertDialogDescription>
              {(confirmDeactivate?.name ?? 'This forwarder')} will be marked inactive. Existing shipments keep their link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!confirmDeactivate) return;
                setForwarderActive.mutate({ forwarderId: confirmDeactivate.id, isActive: false });
              }}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <BackToTopPill />
    </RoleGuard>
  );
}
